/**
 * P3-38: Stats tab 迁移。
 *
 * 逐结构对齐 `js/stats.js:186-246` 的 `classes.tab.StatsTab`：
 * - 遍历 `game.stats.statGroups`，每个 group 渲染 h1 + table.statTable。
 * - 在 selector 内复现旧 update 的副作用顺序：calculate → unlocked → totalResets++ →
 *   取值 → totalResets--。
 */
import { useGameTick } from "../../bridge/useGameTick";
import type { GamePage } from "../../bridge/GameBridge";
import { TooltipProvider } from "../../components/Tooltip/TooltipProvider";

interface StatSnapshot {
  name: string;
  title: string;
  value: string;
}

interface StatGroupSnapshot {
  title: string;
  stats: StatSnapshot[];
}

function getStatsSnapshot(game: GamePage | null): StatGroupSnapshot[] {
  if (!game) {
    return [];
  }

  const statGroups = game.stats?.statGroups as
    | Array<{ title: string; group: Array<Record<string, unknown>> }>
    | undefined;
  if (!Array.isArray(statGroups)) {
    return [];
  }

  return statGroups.map((group) => {
    const stats = group.group.map((stat) => {
      const calculate = stat.calculate as ((game: GamePage) => number | string) | undefined;
      if (calculate) {
        stat.val = calculate(game);
      }

      if ((stat.val as number) != 0) {
        stat.unlocked = true;
      }

      const isTotalResets = stat.name === "totalResets";
      if (isTotalResets) {
        stat.val = (stat.val as number) + 1;
      }

      const unlocked = Boolean(stat.unlocked);
      let title = "???";
      let value = "";
      if (unlocked) {
        title = String(stat.title ?? "");
        const rawVal = stat.val;
        value =
          typeof rawVal === "number"
            ? (game.getDisplayValueExt as (v: number) => string)(rawVal)
            : String(rawVal ?? "");
      }

      if (isTotalResets) {
        stat.val = (stat.val as number) - 1;
      }

      return {
        name: String(stat.name ?? ""),
        title,
        value,
      };
    });

    return {
      title: String(group.title ?? ""),
      stats,
    };
  });
}

function shallowEq(a: StatGroupSnapshot[], b: StatGroupSnapshot[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    const ag = a[i];
    const bg = b[i];
    if (ag.title !== bg.title || ag.stats.length !== bg.stats.length) {
      return false;
    }
    for (let j = 0; j < ag.stats.length; j++) {
      const as = ag.stats[j];
      const bs = bg.stats[j];
      if (as.name !== bs.name || as.title !== bs.title || as.value !== bs.value) {
        return false;
      }
    }
  }
  return true;
}

export default function StatsTab() {
  const groups = useGameTick(
    (game) => getStatsSnapshot(game as GamePage | null),
    shallowEq,
  );

  if (!groups.length) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="tabInner Stats">
        {groups.map((group) => (
          <div key={group.title}>
            <h1>{group.title}</h1>
            <table className="statTable">
              <tbody>
                {group.stats.map((stat) => (
                  <tr key={stat.name}>
                    <td>{stat.title}</td>
                    <td>{stat.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
}
